import React, { useContext, useEffect, useState } from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { Button } from '../../../../catalyst-components/button';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { Id } from '../../../../convex/_generated/dataModel';
import { BaseContext } from '../../../../shared/base-context';
import { Input } from '../../../../catalyst-components/input';
import { Textarea } from '../../../../catalyst-components/textarea';
import { Field, Label } from '../../../../catalyst-components/fieldset';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../../catalyst-components/alert';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  SwatchIcon,
  EyeIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
} from '@heroicons/react/24/outline';

// Widget components for preview
import { MindsetScoresWidget } from '../../../evergreen-chat/components/widgets/MindsetScoresWidget';
import { SkillProgressWidget } from '../../../evergreen-chat/components/widgets/SkillProgressWidget';
import { CompletedSessionsWidget } from '../../../evergreen-chat/components/widgets/CompletedSessionsWidget';
import { SessionRecommendationWidget } from '../../../evergreen-chat/components/widgets/SessionRecommendationWidget';
import { RelevantSessionsWidget } from '../../../evergreen-chat/components/widgets/RelevantSessionsWidget';
import { ActiveSessionWidget } from '../../../evergreen-chat/components/widgets/ActiveSessionWidget';

interface ChangeLogEntry {
  _id: string;
  tool_name: string;
  action: string;
  changes?: string;
  modified_at: string;
  modifier?: { firstName?: string; lastName?: string; email?: string } | null;
}

const CATEGORIES = [
  { value: 'session', label: 'Session Chat' },
  { value: 'evergreen', label: 'Evergreen Chat' },
  { value: 'both', label: 'Both' },
  { value: 'system', label: 'System' },
];

const WIDGET_TYPES = [
  { value: 'mindset_scores', label: 'Mindset Scores (Inner Weather)' },
  { value: 'skill_progress', label: 'Skill Progress' },
  { value: 'session_recommendation', label: 'Session Recommendation' },
  { value: 'active_session', label: 'Active Session' },
  { value: 'completed_sessions', label: 'Completed Sessions' },
  { value: 'relevant_sessions', label: 'Relevant Sessions' },
];

// Sample data for widget previews
const WIDGET_SAMPLE_DATA: Record<string, any> = {
  mindset_scores: {
    neuroticism: { current: 35, previous: 42 },
    well_being: { current: 72, previous: 65 },
    self_efficacy: { current: 68, previous: 60 },
  },
  skill_progress: {
    skill_name: 'Interview Preparation',
    completed_count: 3,
    total_count: 5,
    summary: 'Good progress on behavioral questions and STAR method. Next: technical interview practice.',
  },
  session_recommendation: {
    session: {
      id: 'sample-id',
      display_name: 'Crafting Your Elevator Pitch',
      description: 'Learn to create a compelling 30-second summary of your professional background and career goals.',
      skill_name: 'Networking',
    },
    reasoning: 'Based on your progress in networking skills, this session will help you confidently introduce yourself at events.',
  },
  active_session: {
    session: {
      session_id: 'sample-session-id',
      session_name: 'Building Your Resume',
      skill_name: 'Resume Writing',
    },
    message: 'You have an active session in progress. Continue where you left off to complete the exercises.',
  },
  completed_sessions: {
    completed_sessions: [
      { session_name: 'Understanding Your Strengths', skill_name: 'Self-Assessment', completed_at: '2025-01-15T10:00:00Z' },
      { session_name: 'Setting Career Goals', skill_name: 'Self-Assessment', completed_at: '2025-01-20T14:30:00Z' },
      { session_name: 'Resume Basics', skill_name: 'Resume Writing', completed_at: '2025-02-01T09:00:00Z' },
    ],
  },
  relevant_sessions: {
    sessions: [
      { assistant_id: 'sample-1', session_name: 'Networking Strategies', description: 'Learn effective approaches to building professional connections.', skill_name: 'Networking' },
      { assistant_id: 'sample-2', session_name: 'LinkedIn Optimization', description: 'Optimize your LinkedIn profile to attract recruiters.', skill_name: 'Online Presence' },
    ],
  },
};

/** Renders the actual widget component with sample data */
const WidgetPreview: React.FC<{ widgetType: string }> = ({ widgetType }) => {
  const sampleData = WIDGET_SAMPLE_DATA[widgetType];
  if (!sampleData) return null;

  switch (widgetType) {
    case 'mindset_scores':
      return <MindsetScoresWidget data={sampleData} />;
    case 'skill_progress':
      return <SkillProgressWidget data={sampleData} />;
    case 'session_recommendation':
      return <SessionRecommendationWidget data={sampleData} />;
    case 'active_session':
      return <ActiveSessionWidget data={sampleData} />;
    case 'completed_sessions':
      return <CompletedSessionsWidget data={sampleData} />;
    case 'relevant_sessions':
      return <RelevantSessionsWidget data={sampleData} />;
    default:
      return null;
  }
};

const defaultFormData = {
  name: '',
  display_name: '',
  description: '',
  parameters: JSON.stringify({ type: 'object', properties: {}, required: [] }, null, 2),
  category: 'session',
  produces_widget: false,
  widget_type: '',
};

export const ToolDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { convexUserId } = useContext(BaseContext);

  const isNew = id === 'new';
  const toolId = isNew ? undefined : (id as Id<"ai_tools">);

  const existingTool = useQuery(
    api.ai_tools.getById,
    toolId ? { id: toolId } : "skip"
  );
  const changeLog = useQuery(
    api.ai_tools.getChangeLog,
    toolId ? { toolId } : "skip"
  );
  const feedbackStats = useQuery(
    api.widget_feedback.getStatsForTool,
    existingTool?.name ? { tool_name: existingTool.name } : "skip"
  );

  const createTool = useMutation(api.ai_tools.create);
  const updateTool = useMutation(api.ai_tools.update);

  const [formData, setFormData] = useState(defaultFormData);
  const [jsonError, setJsonError] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [loaded, setLoaded] = useState(false);

  // Load existing tool data into form
  useEffect(() => {
    if (existingTool && !loaded) {
      setFormData({
        name: existingTool.name,
        display_name: existingTool.display_name,
        description: existingTool.description,
        parameters: (() => {
          try {
            return JSON.stringify(JSON.parse(existingTool.parameters), null, 2);
          } catch {
            return existingTool.parameters;
          }
        })(),
        category: existingTool.category,
        produces_widget: existingTool.produces_widget ?? false,
        widget_type: existingTool.widget_type ?? '',
      });
      setLoaded(true);
    }
  }, [existingTool, loaded]);

  const showNotification = (message: string) => {
    setAlertMessage(message);
    setShowAlert(true);
  };

  const validateJson = (json: string): boolean => {
    try {
      JSON.parse(json);
      setJsonError('');
      return true;
    } catch (e: any) {
      setJsonError(e.message);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!convexUserId) return;

    if (!validateJson(formData.parameters)) return;

    try {
      if (isNew) {
        await createTool({
          name: formData.name,
          display_name: formData.display_name,
          description: formData.description,
          parameters: formData.parameters,
          category: formData.category,
          created_by: convexUserId,
        });
        showNotification('Tool created successfully');
      } else if (toolId) {
        await updateTool({
          id: toolId,
          name: formData.name,
          display_name: formData.display_name,
          description: formData.description,
          parameters: formData.parameters,
          category: formData.category,
          updated_by: convexUserId,
        });
        showNotification('Tool updated successfully');
      }
      navigate('../');
    } catch (error: any) {
      showNotification(`Error: ${error.message}`);
    }
  };

  const getActionBadge = (action: string) => {
    const colors: Record<string, string> = {
      created: 'bg-green-100 text-green-800',
      updated: 'bg-blue-100 text-blue-800',
      enabled: 'bg-emerald-100 text-emerald-800',
      disabled: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[action] || 'bg-gray-100 text-gray-700'}`}>
        {action}
      </span>
    );
  };

  // Loading state for edit mode
  if (!isNew && existingTool === undefined) {
    return (
      <div className="min-h-screen bg-background pt-24 flex items-center justify-center">
        <p className="text-muted-foreground">Loading tool...</p>
      </div>
    );
  }

  if (!isNew && existingTool === null) {
    return (
      <div className="min-h-screen bg-background pt-24 flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Tool not found.</p>
          <Button onClick={() => navigate('../')}>Back to Tools</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-primary/5 to-accent/5 px-6 py-8">
        <div>
          <div className="flex items-center gap-4">
            <Button plain onClick={() => navigate('../')}>
              <ArrowLeftIcon className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-1">
                {isNew ? 'New Tool' : `Edit: ${existingTool?.display_name}`}
              </h1>
              <p className="text-base text-muted-foreground">
                {isNew
                  ? 'Define a new AI tool for the chat assistants'
                  : 'Modify this tool\'s definition and parameters'}
              </p>
              {!isNew && existingTool && (
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span>Used <strong className="text-foreground">{existingTool.usage_count || 0}</strong> times</span>
                  {existingTool.last_used_at && (
                    <span>Last used: {new Date(existingTool.last_used_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-8">
        <div className="space-y-8">
          {/* Form */}
          <div className="border rounded-xl bg-white p-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field>
                  <Label>Function Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g. get_user_profile"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    snake_case, must match the function handler name in code
                  </p>
                </Field>
                <Field>
                  <Label>Display Name</Label>
                  <Input
                    value={formData.display_name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({ ...formData, display_name: e.target.value })
                    }
                    placeholder="e.g. Get User Profile"
                    required
                  />
                </Field>
              </div>

              <Field>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Describe what this tool does and when the AI should use it..."
                  rows={4}
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  This is the description the AI sees to decide when to call this tool
                </p>
              </Field>

              <Field>
                <Label>Category</Label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  className="block w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </Field>

              {/* Widget Info (read-only) */}
              {formData.produces_widget && formData.widget_type && (
                <div className="border rounded-lg p-4 bg-violet-50/50">
                  <div className="flex items-center gap-2 mb-1">
                    <SwatchIcon className="h-4 w-4 text-violet-600" />
                    <span className="text-sm font-medium text-foreground">Widget Output</span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800">
                      {WIDGET_TYPES.find((wt) => wt.value === formData.widget_type)?.label || formData.widget_type}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    This tool produces a visible widget in the chat UI when called.
                  </p>

                  {/* Feedback stats */}
                  {feedbackStats && feedbackStats.total > 0 && (
                    <div className="flex items-center gap-4 mb-3 text-sm">
                      <span className="text-xs text-muted-foreground">User feedback:</span>
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <HandThumbUpIcon className="h-3.5 w-3.5" />
                        {feedbackStats.positive}
                      </span>
                      <span className="inline-flex items-center gap-1 text-red-500">
                        <HandThumbDownIcon className="h-3.5 w-3.5" />
                        {feedbackStats.negative}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        ({Math.round((feedbackStats.positive / feedbackStats.total) * 100)}% positive)
                      </span>
                    </div>
                  )}

                  <div className="border-t border-violet-200 pt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <EyeIcon className="h-4 w-4 text-violet-600" />
                      <span className="text-xs font-medium text-violet-700">Preview (sample data)</span>
                    </div>
                    <div className="max-w-sm">
                      <WidgetPreview widgetType={formData.widget_type} />
                    </div>
                  </div>
                </div>
              )}

              <Field>
                <div className="flex items-center justify-between mb-1">
                  <Label>Parameters (JSON Schema)</Label>
                  <div className="flex items-center gap-2">
                    {formData.parameters.trim() && !jsonError && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircleIcon className="h-3.5 w-3.5" /> Valid JSON
                      </span>
                    )}
                    {jsonError && (
                      <span className="text-xs text-red-600 flex items-center gap-1">
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" /> Invalid
                      </span>
                    )}
                    <Button
                      plain
                      type="button"
                      onClick={() => {
                        try {
                          const formatted = JSON.stringify(JSON.parse(formData.parameters), null, 2);
                          setFormData({ ...formData, parameters: formatted });
                          setJsonError('');
                        } catch (e: any) {
                          setJsonError(e.message);
                        }
                      }}
                    >
                      <span className="text-xs">Format</span>
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={formData.parameters}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    const val = e.target.value;
                    setFormData({ ...formData, parameters: val });
                    validateJson(val);
                  }}
                  rows={12}
                  className={`font-mono text-sm ${jsonError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  required
                />
                {jsonError && (
                  <p className="text-xs text-red-600 mt-1">
                    {jsonError}
                  </p>
                )}

                {/* Parsed parameter preview */}
                {!jsonError && formData.parameters.trim() && (() => {
                  try {
                    const params = JSON.parse(formData.parameters);
                    const props = params.properties || {};
                    const required = params.required || [];
                    const propNames = Object.keys(props);
                    if (propNames.length === 0) {
                      return (
                        <p className="text-xs text-muted-foreground mt-2 italic">
                          No parameters — tool takes no arguments.
                        </p>
                      );
                    }
                    return (
                      <div className="mt-3 border rounded-lg p-3 bg-gray-50">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Parameter Preview</p>
                        <div className="space-y-1.5">
                          {propNames.map((name) => {
                            const prop = props[name];
                            const isRequired = required.includes(name);
                            return (
                              <div key={name} className="flex items-start gap-2 text-xs">
                                <code className={`font-mono px-1 py-0.5 rounded shrink-0 ${
                                  isRequired
                                    ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                    : 'bg-white text-gray-700 border border-gray-200'
                                }`}>
                                  {name}{isRequired ? '*' : ''}
                                </code>
                                <span className="text-muted-foreground">
                                  {prop?.type && <span className="text-gray-500">{prop.type}</span>}
                                  {prop?.enum && <span className="text-gray-500"> [{prop.enum.join(', ')}]</span>}
                                  {prop?.description && <span> — {prop.description}</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  } catch {
                    return null;
                  }
                })()}
              </Field>

              <div className="flex gap-3 pt-2">
                <Button type="submit" color="dark">
                  {isNew ? 'Create Tool' : 'Save Changes'}
                </Button>
                <Button plain onClick={() => navigate('../')}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>

          {/* Change Log (edit mode only) */}
          {!isNew && changeLog && changeLog.length > 0 && (
            <div className="border rounded-xl overflow-hidden bg-white">
              <div className="p-4 border-b bg-gray-50">
                <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <ClockIcon className="h-5 w-5" />
                  Change History
                </h2>
              </div>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Action</TableHeader>
                    <TableHeader>Details</TableHeader>
                    <TableHeader>Modified By</TableHeader>
                    <TableHeader>Date</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changeLog.map((change: ChangeLogEntry) => (
                    <TableRow key={change._id}>
                      <TableCell>{getActionBadge(change.action)}</TableCell>
                      <TableCell>
                        {change.changes ? (
                          <p className="text-xs text-muted-foreground max-w-xs truncate" title={change.changes}>
                            {(() => {
                              try {
                                const parsed = JSON.parse(change.changes);
                                return Object.keys(parsed).join(', ');
                              } catch {
                                return change.changes;
                              }
                            })()}
                          </p>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {change.modifier
                            ? `${change.modifier.firstName || ''} ${change.modifier.lastName || ''}`.trim() || change.modifier.email
                            : 'Unknown'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-muted-foreground">
                          {new Date(change.modified_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Notifications */}
      <Alert open={showAlert} onClose={() => setShowAlert(false)}>
        <AlertTitle>Notification</AlertTitle>
        <AlertDescription>{alertMessage}</AlertDescription>
        <AlertActions>
          <Button onClick={() => setShowAlert(false)}>OK</Button>
        </AlertActions>
      </Alert>
    </div>
  );
};
